const CURRENT_USER_KEY = 'bme-current-user';
const DEFAULT_SITE_ID = 'hosp_001';
const SITE_SCOPED_PREFIX = 'bme-';
const NON_SCOPED_KEYS = new Set([CURRENT_USER_KEY]);
const SITE_SCOPED_API_PREFIXES = [
  '/api/assets',
  '/api/call-logs',
  '/api/pm-master-data',
  '/api/pm-calibration-reports/upload',
  '/api/db/users',
  '/api/db/assets',
  '/api/db/call-logs',
  '/api/db/amc-cmc-contracts',
  '/api/db/pm-calibration',
  '/api/db/audit-logs'
];

function parseStoredCurrentUser() {
  try {
    const rawUser = window.localStorage.getItem(CURRENT_USER_KEY);
    return rawUser ? JSON.parse(rawUser) : null;
  } catch (_error) {
    return null;
  }
}

export function getCurrentSiteId() {
  const storedUser = parseStoredCurrentUser();
  const siteId = storedUser?.site_id;

  if (typeof siteId === 'string' && siteId.trim()) {
    return siteId.trim();
  }

  return DEFAULT_SITE_ID;
}

function isScopedStorageKey(key) {
  return typeof key === 'string' && key.startsWith(SITE_SCOPED_PREFIX) && !NON_SCOPED_KEYS.has(key);
}

function getBaseStorageKey(key) {
  if (typeof key !== 'string') {
    return key;
  }

  return key.split('::')[0];
}

function isQuotaExceededError(error) {
  if (!error) {
    return false;
  }

  return (
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    String(error.message || '').toLowerCase().includes('quota')
  );
}

function normalizeExistingScopedKey(key) {
  if (typeof key !== 'string') {
    return key;
  }

  const baseKey = getBaseStorageKey(key);
  if (!isScopedStorageKey(baseKey)) {
    return key;
  }

  const parts = key.split('::').filter(Boolean);
  if (parts.length <= 2) {
    return key;
  }

  const base = parts[0];
  const scopes = parts.slice(1);
  const firstScope = scopes[0];
  const hasRepeatedScopes = scopes.every((scope) => scope === firstScope);

  if (hasRepeatedScopes && firstScope) {
    return `${base}::${firstScope}`;
  }

  return key;
}

function cleanupMalformedScopedStorageKeys(originalGetItem, originalSetItem, originalRemoveItem) {
  try {
    const existingKeys = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const existingKey = window.localStorage.key(index);
      if (!existingKey) continue;
      existingKeys.push(existingKey);
    }

    existingKeys.forEach((existingKey) => {
      const normalizedKey = normalizeExistingScopedKey(existingKey);
      if (normalizedKey === existingKey) {
        return;
      }

      const existingValue = originalGetItem.call(window.localStorage, existingKey);
      if (existingValue === null) {
        originalRemoveItem.call(window.localStorage, existingKey);
        return;
      }

      const normalizedValue = originalGetItem.call(window.localStorage, normalizedKey);
      if (normalizedValue === null) {
        try {
          originalSetItem.call(window.localStorage, normalizedKey, existingValue);
        } catch (_error) {
        }
      }

      originalRemoveItem.call(window.localStorage, existingKey);
    });
  } catch (_error) {
  }
}

function getScopedStorageKey(key, siteId = getCurrentSiteId()) {
  const baseKey = getBaseStorageKey(key);

  if (!isScopedStorageKey(baseKey)) {
    return key;
  }

  return `${baseKey}::${siteId}`;
}

function migrateLegacyValue(key, siteId, originalGetItem, originalSetItem) {
  if (siteId !== DEFAULT_SITE_ID) {
    return;
  }

  const scopedKey = getScopedStorageKey(key, siteId);
  const scopedValue = originalGetItem.call(window.localStorage, scopedKey);

  if (scopedValue !== null) {
    return;
  }

  const legacyValue = originalGetItem.call(window.localStorage, key);
  if (legacyValue !== null) {
    originalSetItem.call(window.localStorage, scopedKey, legacyValue);
  }
}

function shouldScopeApiRequest(url) {
  return SITE_SCOPED_API_PREFIXES.some((prefix) => url.pathname === prefix || url.pathname.startsWith(`${prefix}/`));
}

function withSiteQuery(url, siteId) {
  if (!url.searchParams.has('site_id')) {
    url.searchParams.set('site_id', siteId);
  }
}

function getRequestMethod(init, input) {
  if (init?.method) {
    return String(init.method).toUpperCase();
  }

  if (typeof Request !== 'undefined' && input instanceof Request) {
    return String(input.method || 'GET').toUpperCase();
  }

  return 'GET';
}

function getRequestHeaders(init, input) {
  const headers = new Headers(init?.headers || (typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined) || {});
  return headers;
}

function addSiteIdToJsonBody(body, siteId) {
  if (!body || typeof body !== 'string') {
    return body;
  }

  try {
    const parsedBody = JSON.parse(body);

    if (Array.isArray(parsedBody)) {
      return body;
    }

    if (parsedBody && typeof parsedBody === 'object' && !parsedBody.site_id) {
      return JSON.stringify({ ...parsedBody, site_id: siteId });
    }
  } catch (_error) {
    return body;
  }

  return body;
}

export function installHospitalDataIsolation() {
  if (typeof window === 'undefined' || window.__bmeHospitalIsolationInstalled) {
    return;
  }

  const originalGetItem = Storage.prototype.getItem;
  const originalSetItem = Storage.prototype.setItem;
  const originalRemoveItem = Storage.prototype.removeItem;
  const originalFetch = window.fetch.bind(window);

  cleanupMalformedScopedStorageKeys(originalGetItem, originalSetItem, originalRemoveItem);

  Storage.prototype.getItem = function getItemPatched(key) {
    if (this !== window.localStorage || !isScopedStorageKey(key)) {
      return originalGetItem.call(this, key);
    }

    const siteId = getCurrentSiteId();
    migrateLegacyValue(key, siteId, originalGetItem, originalSetItem);
    return originalGetItem.call(this, getScopedStorageKey(key, siteId));
  };

  Storage.prototype.setItem = function setItemPatched(key, value) {
    if (this !== window.localStorage || !isScopedStorageKey(key)) {
      return originalSetItem.call(this, key, value);
    }

    const siteId = getCurrentSiteId();
    const scopedKey = getScopedStorageKey(key, siteId);

    try {
      return originalSetItem.call(this, scopedKey, value);
    } catch (error) {
      if (!isQuotaExceededError(error)) {
        throw error;
      }

      try {
        const scopedAssetsCacheKey = getScopedStorageKey('bme-assets-cache', siteId);
        const scopedAssetsKey = getScopedStorageKey('bme-assets', siteId);
        originalRemoveItem.call(this, scopedAssetsCacheKey);
        originalRemoveItem.call(this, scopedAssetsKey);
        return originalSetItem.call(this, scopedKey, value);
      } catch (retryError) {
        console.warn('[siteScopedData] localStorage quota exceeded; write skipped for key:', scopedKey, retryError);
        return;
      }
    }
  };

  Storage.prototype.removeItem = function removeItemPatched(key) {
    if (this !== window.localStorage || !isScopedStorageKey(key)) {
      return originalRemoveItem.call(this, key);
    }

    return originalRemoveItem.call(this, getScopedStorageKey(key));
  };

  window.fetch = async function fetchPatched(input, init = undefined) {
    const siteId = getCurrentSiteId();
    const requestUrl = new URL(typeof input === 'string' ? input : input.url, window.location.origin);

    if (!shouldScopeApiRequest(requestUrl)) {
      return originalFetch(input, init);
    }

    const method = getRequestMethod(init, input);
    const headers = getRequestHeaders(init, input);
    headers.set('x-site-id', siteId);

    if (method === 'GET' || method === 'DELETE') {
      withSiteQuery(requestUrl, siteId);
      return originalFetch(requestUrl.toString(), {
        ...(init || {}),
        headers,
      });
    }

    const nextInit = {
      ...(init || {}),
      headers,
    };

    const contentType = headers.get('Content-Type') || headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      nextInit.body = addSiteIdToJsonBody(nextInit.body, siteId);
    }

    return originalFetch(requestUrl.toString(), nextInit);
  };

  window.__bmeHospitalIsolationInstalled = true;
}